import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import {
  AudioRecordingState,
  recordingStateAtom,
  useStopAudioRecording,
} from "../../video-sdk";
import { useSendProviderParticipantStateCallback } from "../messages/useSendProviderParticipantStateCallback";
import { audioRecordingEntitiesAtom } from "./state";
import { useAudioRecorderLifecycleCallbacks } from "./useAudioRecorderLifecycleCallbacks";
import { useUploadAudioCallback } from "./useUploadAudioCallback";

export function useStopRecordingCallback() {
  const handleStop = useStopRecordingTwilioAudio();

  const sendParticipantState = useSendProviderParticipantStateCallback();

  return useStopAudioRecording({
    handleStop,
    handleAfterStop: function handleAfter(shouldBroadcastState: boolean) {
      if (shouldBroadcastState) {
        sendParticipantState();
      }
    },
  });
}

function useStopRecordingTwilioAudio() {
  const getAudioRecordingEntities = useAtomCallback(
    useCallback((get) => get(audioRecordingEntitiesAtom), []),
  );
  const getIsRecording = useAtomCallback(
    useCallback(
      (get) => get(recordingStateAtom).value === AudioRecordingState.RECORDING,
      [],
    ),
  );
  const { tearDownRecording } = useAudioRecorderLifecycleCallbacks();
  const uploadAudio = useUploadAudioCallback();

  return useCallback(
    async (shouldTeardown: boolean) => {
      const audioRecorder = getAudioRecordingEntities()?.audioRecorder;
      if (!audioRecorder) return;
      if (shouldTeardown) {
        await tearDownRecording();
      }

      const wasRecording = getIsRecording();
      const { audioBlob, recordingMetadata } =
        await audioRecorder!.getRecording({
          shouldContinueRecordingAfter: false,
        });
      if (!audioBlob.size) return;
      // Do not await. Uploading the audio should be a fire-and-forget
      // side-effect.
      if (wasRecording) {
        uploadAudio({ audioBlob, recordingMetadata });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
